import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { DollarSign, Wallet, ShieldCheck, ShieldAlert, Loader2, ArrowUpRight, Clock, CheckCircle2, XCircle, Crown } from "lucide-react";
import { api } from "@/api/client";
import { useSession } from "@/context/SessionContext";

// Wallet & Monetization Hub — Cash-Out panel (S20). STAGE 8: live withdrawal
// requests. Reserves earned Paid Coins into an escrow hold via POST /rewards/cashout
// and shows the withdrawal ledger. External disbursement stays queued until the
// compliance milestone + payout provider go live.
const COIN_AUD = 0.10; // 1 Paid Coin = $0.10 AUD
const MIN_COINS = 50;

const ROUTES = [
  { key: "crypto", label: "Milink", Icon: Wallet },
  { key: "paypal", label: "PayPal", Icon: DollarSign },
  { key: "bank", label: "Bank", Icon: DollarSign },
];

const STATUS_STYLE = {
  escrow_held: { label: "Escrow Hold", cls: "text-amber-400 border-amber-500/40", Icon: Clock },
  queued: { label: "Queued", cls: "text-[#00E5FF] border-[#00E5FF]/40", Icon: Clock },
  paid: { label: "Paid", cls: "text-emerald-400 border-emerald-500/40", Icon: CheckCircle2 },
  cancelled: { label: "Cancelled", cls: "text-zinc-500 border-zinc-700", Icon: XCircle },
};

export default function CashOutPanel() {
  const { user } = useSession();
  const isFounder = ["djohnm1972@gmail.com", "alix@misteck.com"].includes((user?.email || "").toLowerCase())
    || ["johnnywalker"].includes((user?.handle || "").toLowerCase())
    || ["founder", "admin"].includes(user?.role);

  const [gate, setGate] = useState(null);
  const [history, setHistory] = useState([]);
  const [escrow, setEscrow] = useState(0);
  const [amount, setAmount] = useState("");
  const [routeType, setRouteType] = useState("crypto");
  const [dest, setDest] = useState("");
  const [bsb, setBsb] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [g, h] = await Promise.all([
        api.get("/rewards/payout-gate"),
        api.get("/rewards/cashout/history"),
      ]);
      setGate(g.data);
      setHistory(h.data?.requests || []);
      setEscrow(Number(h.data?.escrow_balance ?? 0));
    } catch {
      setGate({ error: true });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (gate === null) {
    return (
      <div className="grid place-items-center py-10" data-testid="cashout-loading">
        <Loader2 className="w-5 h-5 text-[#FFCC00] animate-spin" />
      </div>
    );
  }

  const earned = Number(gate.earned_coins ?? 0);
  const followerOk = !!gate.follower_gate_cleared;
  const canRequest = isFounder || followerOk;
  const amt = parseFloat(amount || 0);

  const fee = (() => {
    const gross = amt * COIN_AUD;
    if (routeType === "paypal") return { label: "PayPal Processing Fee (2.5%)", cost: gross * 0.025, currency: "AUD" };
    if (routeType === "bank") return { label: "Bank Wire Flat Fee", cost: 2.50, currency: "AUD" };
    return { label: "Milink Crypto Gas Weight", cost: 0.15, currency: "MILINK" };
  })();
  const netPayout = Math.max(0, amt * COIN_AUD - (fee.currency === "AUD" ? fee.cost : 0));

  const destValid = (() => {
    const d = dest.trim();
    if (routeType === "crypto") return d.length >= 20;
    if (routeType === "paypal") return /\S+@\S+\.\S+/.test(d);
    if (routeType === "bank") return /^\d{6}$/.test(bsb.replace(/[-\s]/g, "")) && /^\d{5,12}$/.test(d.replace(/\s/g, ""));
    return false;
  })();
  const amountValid = amt >= MIN_COINS && amt <= earned;
  const readyToSubmit = canRequest && amountValid && destValid && !submitting;

  const setRoute = (k) => { setRouteType(k); setDest(""); setBsb(""); };

  const submit = async () => {
    if (!readyToSubmit) return;
    setSubmitting(true);
    try {
      const body = { amount: amt, route: routeType, destination: dest.trim() };
      if (routeType === "bank") body.bank_bsb = bsb.trim();
      const r = await api.post("/rewards/cashout", body);
      const status = r.data?.status;
      toast.success(
        status === "queued"
          ? `Cash-out queued for settlement · ${amt} PC reserved`
          : `Request accepted — ${amt} PC held in escrow`,
        { description: status === "queued" ? "Founder QA: gates bypassed." : "Releases once the network milestone clears." }
      );
      setAmount(""); setDest(""); setBsb("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit cash-out request");
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.post(`/rewards/cashout/${id}/cancel`);
      toast.success("Request cancelled — coins returned to your balance");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel request");
    }
  };

  return (
    <div className="w-full text-white select-none" data-testid="cashout-panel">
      {/* Balances */}
      <div className="w-full bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-zinc-400 font-black tracking-widest uppercase">Money You've Earned</span>
          {isFounder && (
            <span data-testid="cashout-founder-badge" className="flex items-center gap-1 text-[8px] font-black uppercase tracking-wider text-black bg-[#FFCC00] px-2 py-0.5 rounded-full">
              <Crown className="w-2.5 h-2.5" /> Founder QA
            </span>
          )}
        </div>
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-white text-base font-black uppercase">Paid Coin Balance</h3>
            <span className="text-[9px] text-zinc-400 font-mono">$0.10 AUD per coin · min {MIN_COINS} PC per cash-out</span>
          </div>
          <div className="text-right">
            <span className="text-[#FFCC00] font-mono font-black text-xl block" data-testid="cashout-balance">{earned} PC</span>
            <span className="text-zinc-300 text-xs font-bold font-mono">${(earned * COIN_AUD).toFixed(2)} AUD</span>
          </div>
        </div>
        {escrow > 0 && (
          <div className="mt-3 flex justify-between items-center bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
            <span className="text-amber-300 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5"><Clock className="w-3 h-3" /> Held in Escrow</span>
            <span className="text-amber-300 font-mono font-black text-sm" data-testid="cashout-escrow-balance">{escrow} PC · ${(escrow * COIN_AUD).toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Creator gate lock (non-founder, no follower gate) */}
      {!canRequest && (
        <div className="w-full bg-zinc-950 border border-red-500/30 p-3 rounded-xl flex items-start gap-2.5 mb-4" data-testid="cashout-gate-locked">
          <ShieldAlert className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <p className="text-zinc-300 text-[10px] font-medium leading-relaxed">
            Cash-out requires a minimum of <span className="text-white font-bold">{gate.follower_threshold ?? 100} followers</span>. You have <span className="text-red-400 font-bold">{gate.followers ?? 0}</span>. Grow your base to unlock withdrawals.
          </p>
        </div>
      )}

      {/* Request form */}
      <fieldset disabled={!canRequest} className={!canRequest ? "opacity-50 pointer-events-none" : ""}>
        {/* Amount */}
        <div className="w-full bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4 mb-4">
          <label className="text-zinc-300 text-[9px] font-black tracking-wider uppercase block mb-1.5">Coins to Cash Out</label>
          <div className="relative">
            <input
              type="number" min={MIN_COINS} max={earned} value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder={`${MIN_COINS}`}
              data-testid="cashout-amount"
              className="w-full h-12 bg-black border border-zinc-800 rounded-xl px-4 text-sm text-white font-mono font-bold focus:outline-none focus:border-[#FFCC00] transition-colors"
            />
            <button onClick={() => setAmount(String(earned))} data-testid="cashout-max"
              className="absolute right-3 top-2.5 bg-zinc-900 text-zinc-300 hover:text-white font-bold text-[9px] uppercase px-2 py-1 rounded border border-zinc-800 transition-colors">Max</button>
          </div>
          {amount !== "" && !amountValid && (
            <p className="text-red-400 text-[9px] font-bold mt-1.5">{amt < MIN_COINS ? `Minimum ${MIN_COINS} PC` : "Exceeds your balance"}</p>
          )}
        </div>

        {/* Route */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {ROUTES.map((r) => {
            const on = routeType === r.key;
            const Icon = r.Icon;
            return (
              <button key={r.key} onClick={() => setRoute(r.key)} data-testid={`cashout-route-${r.key}`}
                className={`h-11 rounded-xl font-black text-[10px] uppercase flex flex-col items-center justify-center transition-all ${on ? "bg-zinc-900 border-2 border-[#FFCC00] text-white" : "bg-[#0D0D0D] border border-zinc-900 text-zinc-400"}`}>
                {Icon ? <Icon className="w-3.5 h-3.5 mb-0.5" /> : null}{r.label}
              </button>
            );
          })}
        </div>

        {/* Destination */}
        <div className="w-full bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4 mb-4 space-y-3">
          {routeType === "bank" && (
            <div>
              <label className="text-zinc-300 text-[9px] font-black tracking-wider uppercase block mb-1.5">BSB</label>
              <input value={bsb} onChange={(e) => setBsb(e.target.value)} placeholder="062-000" data-testid="cashout-bank-bsb"
                className="w-full h-11 bg-black border border-zinc-800 rounded-xl px-4 text-sm text-white font-mono focus:outline-none focus:border-[#FFCC00] transition-colors" />
            </div>
          )}
          <div>
            <label className="text-zinc-300 text-[9px] font-black tracking-wider uppercase block mb-1.5">
              {routeType === "crypto" ? "Milink Wallet Address" : routeType === "paypal" ? "PayPal Email" : "Bank Account Number"}
            </label>
            <input value={dest} onChange={(e) => setDest(e.target.value)}
              placeholder={routeType === "crypto" ? "MLK1a2b3c..." : routeType === "paypal" ? "you@example.com" : "12345678"}
              data-testid="cashout-destination"
              className="w-full h-11 bg-black border border-zinc-800 rounded-xl px-4 text-sm text-white font-mono focus:outline-none focus:border-[#FFCC00] transition-colors" />
          </div>
        </div>

        {/* Fee breakdown */}
        {amt > 0 && (
          <div className="w-full bg-[#0A0A0A] border border-zinc-900 rounded-xl p-4 space-y-2 mb-4" data-testid="cashout-fees">
            <div className="flex justify-between text-[11px] font-bold text-zinc-300">
              <span>Gross Conversion Value:</span>
              <span className="font-mono text-white">${(amt * COIN_AUD).toFixed(2)} AUD</span>
            </div>
            <div className="flex justify-between text-[11px] font-bold text-red-400">
              <span>{fee.label}:</span>
              <span className="font-mono">-{fee.currency === "AUD" ? "$" : ""}{fee.cost.toFixed(2)} {fee.currency}</span>
            </div>
            <div className="border-t border-zinc-800 pt-2 flex justify-between text-xs font-black text-[#FFCC00]">
              <span>Estimated Net Transfer:</span>
              <span className="font-mono" data-testid="cashout-net">${netPayout.toFixed(2)} AUD</span>
            </div>
          </div>
        )}

        {/* Submit */}
        <button type="button" onClick={submit} disabled={!readyToSubmit} data-testid="cashout-submit"
          className={`w-full h-12 font-black text-[11px] uppercase tracking-widest rounded-xl flex items-center justify-center gap-2 transition-all ${readyToSubmit ? "bg-[#FFCC00] text-black active:scale-[0.98]" : "bg-zinc-900/80 border border-zinc-800 text-zinc-500 cursor-not-allowed"}`}>
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
          {submitting ? "Submitting..." : "Request Cash-Out"}
        </button>
      </fieldset>

      <div className="flex items-center gap-1.5 mt-2 justify-center text-[8px] text-zinc-500 font-bold tracking-tight text-center">
        <ShieldCheck className="w-3 h-3 text-emerald-600" />
        Coins reserve into escrow instantly · disbursement releases on compliance clearance.
      </div>

      {/* Withdrawal ledger */}
      {history.length > 0 && (
        <div className="mt-6" data-testid="cashout-history">
          <h4 className="text-zinc-300 text-[10px] font-black tracking-widest uppercase mb-2">Your Cash-Out History</h4>
          <div className="space-y-2">
            {history.map((h) => {
              const s = STATUS_STYLE[h.status] || STATUS_STYLE.escrow_held;
              const SIcon = s.Icon;
              return (
                <div key={h.id} data-testid={`cashout-history-item-${h.id}`}
                  className="w-full bg-[#0A0A0A] border border-zinc-900 rounded-xl p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white text-xs font-black">{h.amount_coins} PC</span>
                        <span className="text-zinc-400 text-[9px] font-mono uppercase">{h.route}</span>
                      </div>
                      <span className="text-zinc-400 text-[9px] font-mono">{h.destination_masked}</span>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full border ${s.cls}`}>
                        <SIcon className="w-2.5 h-2.5" /> {s.label}
                      </span>
                      <span className="block text-zinc-300 text-[10px] font-mono font-bold mt-1">${Number(h.net_aud).toFixed(2)} AUD</span>
                    </div>
                  </div>
                  {(h.status === "escrow_held" || h.status === "queued") && (
                    <button onClick={() => cancel(h.id)} data-testid={`cashout-cancel-${h.id}`}
                      className="mt-2 w-full h-8 bg-zinc-900 border border-zinc-800 hover:border-red-500/50 text-zinc-400 hover:text-red-400 font-bold text-[9px] uppercase rounded-lg transition-colors">
                      Cancel & Release Coins
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
